import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import StorefrontLayout from '../components/layout/StorefrontLayout';
import { useAuth } from '../context/AuthContext';
import { buildLoginUrl, getReturnPathForAuthRedirect } from '../utils/authReturnUrl';
import { formatOrderReference } from '../utils/formatOrderReference';
import { applyContentLocaleToUrl } from '../utils/contentLocaleQuery';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type MyOrder = { id: string; createdAt: string };
type MyFeedback = {
  id: string;
  type: 'FEEDBACK' | 'COMPLAINT';
  status: 'NEW' | 'IN_REVIEW' | 'RESOLVED' | 'REJECTED';
  subject?: string | null;
  message: string;
  orderId?: string | null;
  resolutionNote?: string | null;
  createdAt: string;
  updatedAt: string;
};

function feedbackTypeLabel(t: (k: string) => string, type: MyFeedback['type']) {
  return type === 'COMPLAINT' ? t('public.feedback_status_complaint') : t('public.feedback_status_feedback');
}

function feedbackStatusLabel(t: (k: string) => string, status: MyFeedback['status']) {
  switch (status) {
    case 'NEW':
      return t('public.feedback_status_new');
    case 'IN_REVIEW':
      return t('public.feedback_status_in_review');
    case 'RESOLVED':
      return t('public.feedback_status_resolved');
    case 'REJECTED':
      return t('public.feedback_status_rejected');
    default:
      return status;
  }
}

export default function FeedbackPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, auth, authorizedFetch } = useAuth();
  const isCustomer = isAuthenticated && auth.type === 'CUSTOMER';

  const [type, setType] = React.useState<'FEEDBACK' | 'COMPLAINT'>('FEEDBACK');
  const [email, setEmail] = React.useState('');
  const [subject, setSubject] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [orderId, setOrderId] = React.useState('');
  const [orders, setOrders] = React.useState<MyOrder[]>([]);
  const [history, setHistory] = React.useState<MyFeedback[]>([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const loadCustomerData = React.useCallback(async () => {
    if (!isCustomer) return;
    setHistoryLoading(true);
    let nextOrders: MyOrder[] = [];
    let nextHistory: MyFeedback[] = [];
    try {
      const [ordersRes, historyRes] = await Promise.all([
        authorizedFetch(`${API_BASE}/sales/orders/me`),
        authorizedFetch(`${API_BASE}/customer/feedback/me`),
      ]);
      if (ordersRes.ok) {
        nextOrders = (await ordersRes.json()) as MyOrder[];
      }
      if (historyRes.ok) {
        nextHistory = (await historyRes.json()) as MyFeedback[];
      }
    } catch {
      // ignore
    } finally {
      setOrders(nextOrders);
      setHistory(nextHistory);
      setHistoryLoading(false);
    }
  }, [authorizedFetch, isCustomer]);

  React.useEffect(() => {
    void loadCustomerData();
  }, [loadCustomerData]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = isCustomer
        ? await authorizedFetch(`${API_BASE}/customer/feedback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type,
              subject,
              message,
              orderId: type === 'COMPLAINT' ? orderId : undefined,
            }),
          })
        : await fetch(
            applyContentLocaleToUrl(
              `${API_BASE}/public/feedback`,
              i18n.resolvedLanguage || i18n.language,
            ),
            {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email,
              subject,
              message,
            }),
            },
          );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message || t('public.feedback_submit_error'));
      }
      setSuccess(t('public.feedback_sent_success'));
      setEmail('');
      setSubject('');
      setMessage('');
      setOrderId('');
      setType('FEEDBACK');
      await loadCustomerData();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('public.feedback_submit_error'));
    } finally {
      setBusy(false);
    }
  };

  const dateLocale = i18n.resolvedLanguage || i18n.language || undefined;

  return (
    <StorefrontLayout contentContained>
      <section className="py-4">
        <h1 className="text-3xl font-bold text-slate-900">{t('public.feedback_page_title')}</h1>
        <p className="mt-2 text-sm text-slate-600">{t('public.feedback_page_intro')}</p>

        <div className="mt-5 grid items-start gap-6 xl:grid-cols-2">
          <form onSubmit={onSubmit} className="grid gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            {!isCustomer ? (
              <p className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
                {t('public.feedback_guest_hint')}
              </p>
            ) : null}
            {error ? <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p> : null}
            {success ? <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{success}</p> : null}
            <label className="text-xs font-semibold text-slate-700">
              {t('public.feedback_type_label')}
              <select
                value={type}
                onChange={(e) => setType((e.target.value as 'FEEDBACK' | 'COMPLAINT') || 'FEEDBACK')}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="FEEDBACK">{t('public.feedback_type_general')}</option>
                <option value="COMPLAINT">{t('public.feedback_type_complaint')}</option>
              </select>
            </label>
            {!isCustomer && type === 'COMPLAINT' ? (
              <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                {t('public.feedback_complaint_signin_hint')}
              </p>
            ) : null}
            {!isCustomer ? (
              <label className="text-xs font-semibold text-slate-700">
                {t('public.feedback_email')}
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder={t('public.fab_placeholder_email')}
                  required
                />
                <span className="mt-1 block text-[11px] font-normal text-slate-500">{t('public.feedback_email_hint')}</span>
              </label>
            ) : null}
            {isCustomer && type === 'COMPLAINT' ? (
              <label className="text-xs font-semibold text-slate-700">
                {t('public.feedback_order')}
                <select
                  value={orderId}
                  onChange={(e) => setOrderId(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  required
                >
                  <option value="">{t('public.feedback_order_placeholder')}</option>
                  {orders.map((o) => (
                    <option key={o.id} value={o.id}>
                      {formatOrderReference(o.id)} — {new Date(o.createdAt).toLocaleString(dateLocale)}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label className="text-xs font-semibold text-slate-700">
              {t('public.feedback_subject')}
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder={t('public.feedback_subject_ph')}
              />
            </label>
            <label className="text-xs font-semibold text-slate-700">
              {t('public.feedback_message')}
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={7}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder={t('public.feedback_message_ph')}
                required
              />
            </label>
            <div>
              <button type="submit" disabled={busy} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                {busy ? t('public.feedback_sending') : t('public.feedback_send')}
              </button>
              {!isCustomer ? (
                <button
                  type="button"
                  onClick={() => navigate(buildLoginUrl(getReturnPathForAuthRedirect()))}
                  className="ml-3 text-xs font-semibold text-slate-600 hover:text-slate-900"
                >
                  {t('public.feedback_signin_instead')}
                </button>
              ) : null}
            </div>
          </form>

          {isCustomer ? (
            <section>
              <h2 className="text-xl font-semibold text-slate-900">{t('public.feedback_history_title')}</h2>
              <p className="mt-1 text-sm text-slate-600">{t('public.feedback_history_intro')}</p>
              <div className="mt-3 space-y-3">
                {historyLoading ? (
                  <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
                    {t('public.feedback_history_loading')}
                  </div>
                ) : history.length === 0 ? (
                  <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
                    {t('public.feedback_history_empty')}
                  </div>
                ) : (
                  history.map((item) => (
                    <article key={item.id} className="workspace-stat-card">
                      <div className="flex flex-wrap items-center gap-2 text-xs">
                        <span
                          className={`rounded-full px-2 py-0.5 font-semibold ${
                            item.type === 'COMPLAINT' ? 'bg-rose-100 text-rose-800' : 'bg-slate-100 text-slate-800'
                          }`}
                        >
                          {feedbackTypeLabel(t, item.type)}
                        </span>
                        <span
                          className={`rounded-full px-2 py-0.5 font-semibold ${
                            item.status === 'RESOLVED'
                              ? 'bg-emerald-100 text-emerald-800'
                              : item.status === 'REJECTED'
                                ? 'bg-red-100 text-red-800'
                                : item.status === 'IN_REVIEW'
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {feedbackStatusLabel(t, item.status)}
                        </span>
                        <span className="text-slate-500">{new Date(item.createdAt).toLocaleString(dateLocale)}</span>
                      </div>
                      <h3 className="mt-2 text-sm font-semibold text-slate-900">
                        {item.subject || t('public.feedback_no_subject')}
                      </h3>
                      {item.orderId ? (
                        <p className="mt-1 text-xs text-slate-600">
                          {t('public.feedback_order_prefix')} {formatOrderReference(item.orderId)}
                        </p>
                      ) : null}
                      <p className="mt-2 whitespace-pre-wrap text-sm text-slate-800">{item.message}</p>
                      {item.resolutionNote ? (
                        <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                          <p className="font-semibold">{t('public.feedback_note_label')}</p>
                          <p className="mt-1 whitespace-pre-wrap">{item.resolutionNote}</p>
                        </div>
                      ) : null}
                    </article>
                  ))
                )}
              </div>
            </section>
          ) : null}
        </div>
      </section>
    </StorefrontLayout>
  );
}
